import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  Chip,
  Button,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import debounce from 'lodash/debounce';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Types
import { Company } from '../../../types/Company';
import { Carrier } from '../../../types/Carrier';
import { CompanyTypes } from '../../../types/CompanyTypes';
import { FilterableTypes } from '../../../types/FilterableTypes';

// Services
import { addOrUpdateCompany, getCompanyById, getCompanyLogo, uploadCompanyLogo } from '../../../services/companies.services';
import { getCarriers } from '../../../services/facility.service';

// Hooks / utils
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';

interface AddOrUpdateCompanyProps {
  company?: Company | null;
  companyId?: string | null;
  initialCarrierId?: string | null;
  onSaved?: (savedCompany?: Company) => void;
  onCancel?: () => void;
  onRegisterSubmit?: (submitFn: () => Promise<void>) => void;
  onFormChange?: (isValid: boolean, isSubmitting: boolean) => void;
}

type FormValues = {
  id?: string;
  name: string;
  companyTypeId: number;
  carrierId: string;
  carrierIataCode: string;
  brokerCode: string;
  domains: string[];
};

// Basic domain validation (e.g. example.com, sub.example.co.uk)
const DOMAIN_REGEX = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required('Company name is required'),
  companyTypeId: Yup.number().typeError('Company Type is required').required('Company Type is required'),
  carrierId: Yup.string().nullable().notRequired(),
  carrierIataCode: Yup.string().nullable().notRequired(),
  brokerCode: Yup.string().nullable().notRequired(),
  domains: Yup.array()
    .of(Yup.string().trim().lowercase().matches(DOMAIN_REGEX, 'Enter a valid domain (e.g. example.com)'))
    .test('unique-domains', 'Duplicate domains are not allowed', (value) => {
      if (!value || value.length === 0) return true;
      const lower = value.map((v) => String(v).trim().toLowerCase());
      return new Set(lower).size === lower.length;
    }),
});

const AddOrUpdateCompany: React.FC<AddOrUpdateCompanyProps> = ({ company, companyId, initialCarrierId, onSaved, onRegisterSubmit, onFormChange }) => {
  const [loading, setLoading] = useState<boolean>(!!companyId && !company);
  const [carrierOptions, setCarrierOptions] = useState<Carrier[]>([]);
  const [loadingCarriers, setLoadingCarriers] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string>('');

  const { notifyMessage } = useNotifyMessage();

  const LOGO_SIZE = 40;

  const resizeImageToLogo = (file: File): Promise<{ blob: Blob; previewUrl: string }> => {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        // Center-crop to a square first so we don't distort non-square images
        const cropSize = Math.min(img.width, img.height);
        const cropX = (img.width - cropSize) / 2;
        const cropY = (img.height - cropSize) / 2;

        const canvas = document.createElement('canvas');
        canvas.width = LOGO_SIZE;
        canvas.height = LOGO_SIZE;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Canvas not supported'));
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, cropX, cropY, cropSize, cropSize, 0, 0, LOGO_SIZE, LOGO_SIZE);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) {
            reject(new Error('Failed to generate resized image'));
            return;
          }
          resolve({ blob, previewUrl: canvas.toDataURL('image/png') });
        }, 'image/png');
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not read the selected file as an image'));
      };

      img.src = objectUrl;
    });
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setLogoError('');

    try {
      const { blob, previewUrl } = await resizeImageToLogo(file);
      const resizedFile = new File([blob], `logo-${LOGO_SIZE}x${LOGO_SIZE}.png`, { type: 'image/png' });
      setLogoFile(resizedFile);
      setLogoPreviewUrl(previewUrl);
    } catch (err) {
      console.error('resizeImageToLogo error', err);
      setLogoError('Could not process the selected image. Please try a different file.');
      setLogoFile(null);
      setLogoPreviewUrl(null);
    }
  };

  useEffect(() => {
    return () => {
      if (logoPreviewUrl && logoPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  const formik = useFormik<FormValues>({
    initialValues: {
      id: undefined,
      name: '',
      companyTypeId: 0,
      carrierId: '',
      carrierIataCode: '',
      brokerCode: '',
      domains: [],
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload: Partial<Company> & { domains?: string[] } = {
          name: values.name,
          companyTypeId: values.companyTypeId,
          carrierId: values.carrierId,
          carrierIataCode: values.carrierIataCode,
          brokerCode: values.brokerCode,
          domains: (values.domains || []).map((d) => d.trim().toLowerCase()),
          ...(values.id ? { id: values.id } : {}),
        };
        const res: any = await addOrUpdateCompany(payload as Company);

        if (res && (res.status === 200 || res.status === 201)) {
          const savedCompanyId: string | undefined = res.data?.id ?? values.id;
          if (logoFile && savedCompanyId) {
            try {
              await uploadCompanyLogo(savedCompanyId, logoFile);
            } catch (logoErr) {
              console.error('uploadCompanyLogo error', logoErr);
              notifyMessage('Company saved, but the logo failed to upload', 'error');
            }
          }

          notifyMessage('Company saved successfully', 'success');
          onSaved?.(res.data);
        } else if (res && res.status === 409) {
          // A domain is already assigned to another company
          const message = res?.data?.message || 'One or more email domains are already assigned to another company';
          formik.setFieldError('domains', message);
          notifyMessage(message, 'error');
        } else {
          notifyMessage(res?.data?.message || 'Failed to save company', 'error');
        }
      } catch (err: any) {
        console.error('addOrUpdateCompany error', err);
        if (err?.response?.status === 409) {
          const message = err.response?.data?.message || 'One or more email domains are already assigned to another company';
          formik.setFieldError('domains', message);
          notifyMessage(message, 'error');
        } else {
          notifyMessage('Failed to save company', 'error');
        }
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch company for edit
  useEffect(() => {
    let isMounted = true;

    const fetchCompany = async (id: string) => {
      try {
        setLoading(true);
        const res: any = await getCompanyById(id);
        if (res && res.status === 200 && isMounted) {
          const data: Company = res.data;
          formik.setValues({
            id: data.id,
            name: data.name || '',
            companyTypeId: data.companyTypeId ?? 0,
            carrierId: (data as any).carrierId ?? '',
            carrierIataCode: (data as any).carrierIataCode ?? '',
            brokerCode: (data as any).brokerCode ?? '',
            domains: (data as any).domains ?? [],
          });
          await fetchExistingLogo(id, isMounted);
        } else if (isMounted) {
          notifyMessage('Failed to load company details', 'error');
        }
      } catch (err) {
        if (isMounted) {
          console.error('fetchCompany error', err);
          notifyMessage('Failed to load company details', 'error');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const fetchExistingLogo = async (id: string, stillMounted: boolean) => {
      try {
        const blob = await getCompanyLogo(id);
        if (stillMounted && blob && blob.size > 0) {
          const objectUrl = URL.createObjectURL(blob);
          setLogoPreviewUrl(objectUrl);
        }
      } catch (logoErr) {
        // no logo set yet, or fetch failed — safe to ignore
        console.warn('getCompanyLogo error', logoErr);
      }
    };
    if (!company && companyId) {
      fetchCompany(companyId);
    } else if (company) {
      formik.setValues({
        id: company.id,
        name: company.name || '',
        companyTypeId: company.companyTypeId ?? 0,
        carrierId: (company as any).carrierId ?? '',
        carrierIataCode: (company as any).carrierIataCode ?? '',
        brokerCode: company.brokerCode ?? '',
        domains: (company as any).domains ?? [],
      });
      if (company.id) {
        fetchExistingLogo(company.id, isMounted);
      }
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [company, companyId]);
  // Register submit fn
  useEffect(() => {
    if (onRegisterSubmit) {
      onRegisterSubmit(() => formik.submitForm());
    }
  }, [formik]);

  useEffect(() => {
    if (onFormChange) onFormChange(Boolean(formik.isValid && formik.dirty), formik.isSubmitting);
  }, [formik.isValid, formik.isSubmitting, formik.dirty]);

  // Carrier fetch
  const fetchCarriers = useCallback(async (value?: string) => {
    setLoadingCarriers(true);
    try {
      const req: any = {
        skip: 0,
        take: 50,
        filters: value ? [{ carrierCode: value, filterType: FilterableTypes.Contains }] : null,
      } as any;
      const res: any = await getCarriers(req);
      let items: Carrier[] = [];
      if (res && res.status === 200) {
        if (Array.isArray(res.data?.results)) items = res.data.results;
        else if (Array.isArray(res.data)) items = res.data;
      }
      setCarrierOptions((prev) => {
        const ids = new Set(prev.map((p) => String(p.id)));
        const merged = [...prev];
        items.forEach((it) => {
          if (!ids.has(String(it.id))) merged.push(it);
        });
        return merged;
      });
    } catch (err) {
      console.error('fetchCarriers error', err);
    } finally {
      setLoadingCarriers(false);
    }
  }, []);

  const debouncedFetchCarriers = useMemo(() => debounce((val: string) => fetchCarriers(val), 600), [fetchCarriers]);

  useEffect(() => {
    return () => {
      debouncedFetchCarriers.cancel();
    };
  }, [debouncedFetchCarriers]);

  useEffect(() => {
    if (!initialCarrierId) return;
    let active = true;

    (async () => {
      // check already-loaded options first
      const exist = carrierOptions.find((c) => String(c.id) === String(initialCarrierId));
      if (exist) {
        setSelectedCarrier(exist);
        formik.setFieldValue('carrierId', exist.id ?? '');
        formik.setFieldValue('carrierIataCode', exist.carrierCode ?? exist.iataCode ?? '');
        return;
      }

      // fetch page and try to find the carrier by id
      try {
        setLoadingCarriers(true);
        const res: any = await getCarriers({ skip: 0, take: 50 } as any);
        if (!active) return;
        const items: Carrier[] = Array.isArray(res?.data?.results) ? res.data.results : Array.isArray(res?.data) ? res.data : [];
        // merge fetched items
        setCarrierOptions((prev) => {
          const ids = new Set(prev.map((p) => String(p.id)));
          const merged = [...prev];
          items.forEach((it) => {
            if (!ids.has(String(it.id))) merged.push(it);
          });
          return merged;
        });

        const found = items.find((c) => String(c.id) === String(initialCarrierId));
        if (found) {
          setSelectedCarrier(found);
          formik.setFieldValue('carrierId', found.id ?? '');
          formik.setFieldValue('carrierIataCode', found.carrierCode ?? found.iataCode ?? '');
        } else {
          const synthetic: Carrier = {
            id: initialCarrierId,
            carrierCode: initialCarrierId,
            iataCode: initialCarrierId,
            name: '',
          } as Carrier;
          setSelectedCarrier(synthetic);
          setCarrierOptions((prev) => (prev.some((p) => String(p.id) === String(synthetic.id)) ? prev : [synthetic, ...prev]));
          formik.setFieldValue('carrierId', synthetic.id ?? '');
          formik.setFieldValue('carrierIataCode', synthetic.carrierCode ?? synthetic.iataCode ?? '');
        }
      } catch (err) {
        console.error('Failed to load carriers for initialCarrierId', err);
      } finally {
        if (active) setLoadingCarriers(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [initialCarrierId]);

  // to keep selected carrier in sync when formik values change
  useEffect(() => {
    const formCarrierId = formik.values.carrierId;
    if (!formCarrierId) {
      setSelectedCarrier(null);
      return;
    }
    const found = carrierOptions.find((c) => String(c.id) === String(formCarrierId));
    if (found) setSelectedCarrier(found);
  }, [formik.values.carrierId, carrierOptions]);

  useEffect(() => {
    if (selectedCarrier) {
      const code = (selectedCarrier as any).carrierCode ?? (selectedCarrier as any).iataCode ?? '';
      setInputValue(String(code));
    } else {
      setInputValue('');
    }
  }, [selectedCarrier]);

  if (loading && !formik.values.name && companyId) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  // company types
  const companyTypeOptions = Object.keys(CompanyTypes)
    .filter((k) => isNaN(Number(k)))
    .map((key) => ({ label: key, value: (CompanyTypes as any)[key] as number }));

  const carrierError = Boolean(formik.touched.carrierId && formik.errors.carrierId);
  const carrierHelper = formik.touched.carrierId && formik.errors.carrierId ? String(formik.errors.carrierId) : '';

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12} mt={4}>
          <TextField
            id='name'
            name='name'
            label='Company Name'
            fullWidth
            size='small'
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.name && formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name ? String(formik.errors.name) : ''}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size='small' error={Boolean(formik.touched.companyTypeId && formik.errors.companyTypeId)}>
            <InputLabel id='company-type-label'>Company Type</InputLabel>
            <Select
              labelId='company-type-label'
              id='companyTypeId'
              name='companyTypeId'
              label='Company Type'
              value={typeof formik.values.companyTypeId === 'number' ? formik.values.companyTypeId : ''}
              onChange={(e) => formik.setFieldValue('companyTypeId', Number(e.target.value))}
              onBlur={formik.handleBlur}
            >
              {companyTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{formik.touched.companyTypeId && formik.errors.companyTypeId ? String(formik.errors.companyTypeId) : ''}</FormHelperText>
          </FormControl>
        </Grid>

        {/* Carrier Autocomplete */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id='carrier-autocomplete'
            options={carrierOptions}
            getOptionLabel={(option) => {
              if (!option) return '';
              const code = (option as any).carrierCode ?? (option as any).iataCode ?? '';
              const name = (option as any).name ?? '';
              return name ? `${code} — ${name}` : `${code}`;
            }}
            isOptionEqualToValue={(option, value) => String(option.id) === String((value as any)?.id)}
            loading={loadingCarriers}
            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
              const upper = (newInputValue ?? '').toUpperCase();
              if (reason === 'input') {
                setInputValue(upper);
                if (upper.length >= 1) debouncedFetchCarriers(upper);
              } else if (reason === 'clear') {
                setInputValue('');
                setCarrierOptions([]);
              }
            }}
            value={selectedCarrier}
            onChange={(_, value: Carrier | null) => {
              setSelectedCarrier(value);
              if (value) {
                formik.setFieldValue('carrierId', value.id ?? '');
                formik.setFieldValue('carrierIataCode', value.carrierCode ?? value.iataCode ?? '');
              } else {
                formik.setFieldValue('carrierId', '');
                formik.setFieldValue('carrierIataCode', '');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Carrier (IATA)'
                size='small'
                onBlur={formik.handleBlur}
                error={carrierError}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCarriers ? <CircularProgress color='inherit' size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            clearOnEscape
          />
          <FormHelperText error={carrierError} style={{ marginTop: 4 }}>
            {carrierHelper}
          </FormHelperText>
        </Grid>

        <Grid item xs={12}>
          <TextField
            id='brokerCode'
            name='brokerCode'
            label='Broker Code'
            fullWidth
            size='small'
            value={formik.values.brokerCode ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.brokerCode && formik.errors.brokerCode)}
            helperText={formik.touched.brokerCode && formik.errors.brokerCode ? String(formik.errors.brokerCode) : ''}
          />
        </Grid>

        {/* Email Domains */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            freeSolo
            id='domains'
            options={[]}
            value={formik.values.domains}
            onChange={(_, newValue) => {
              // normalize (trim + lowercase) and drop duplicates/empties
              const normalized = Array.from(new Set((newValue as string[]).map((v) => String(v).trim().toLowerCase()).filter((v) => v.length > 0)));
              formik.setFieldValue('domains', normalized);
              formik.setFieldTouched('domains', true, false);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} variant='outlined' label={option} size='small' {...tagProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label='Email Domains'
                placeholder='Type a domain and press Enter'
                size='small'
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.domains && formik.errors.domains)}
                helperText={
                  (formik.touched.domains && formik.errors.domains
                    ? Array.isArray(formik.errors.domains)
                      ? formik.errors.domains.find((e) => Boolean(e)) || 'Enter a valid domain (e.g. example.com)'
                      : String(formik.errors.domains)
                    : '') || 'Optional. Press Enter to add a domain (e.g. example.com). A domain can only belong to one company.'
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Box display='flex' alignItems='center' gap={2}>
            <Avatar variant='rounded' src={logoPreviewUrl ?? undefined} sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}>
              {!logoPreviewUrl && <ImageOutlinedIcon fontSize='small' />}
            </Avatar>
            <Button variant='outlined' component='label' size='small'>
              Upload Logo
              <input type='file' accept='image/png,image/jpeg,image/svg+xml' hidden onChange={handleLogoChange} />
            </Button>
          </Box>
          <FormHelperText error={Boolean(logoError)} style={{ marginTop: 4 }}>
            {logoError || 'Optional. Image will be cropped to a square and resized to 40x40px.'}
          </FormHelperText>
        </Grid>
      </Grid>
    </form>
  );
};

export default AddOrUpdateCompany;
